import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";

// Schema ultra-permissif pour l'autocomplete
const SsqAutocompleteSchema = z.array(z.any()).transform((items) => {
    return items
        .map((item: any) => ({
            id: item.id || 0,
            title: item.title || "Sans titre",
            displayTitle: item.displayTitle || null,
            ...item, // Garde toutes les autres propriétés
        }))
        .filter((item: any) => typeof item.id === "number" && typeof item.title === "string");
});

export const searchSsqApiKey = "searchSsq";

export interface UseSearchSsqOptions {
    searchTerm?: string;
    enabled?: boolean;
    page?: number;
    itemsPerPage?: number;
    entityType?: "company" | "product" | "packaging";
    publishedOnly?: boolean;
}

export const useSearchSsq = (options: UseSearchSsqOptions = {}) => {
    const { searchTerm = "", enabled = true, page = 1, itemsPerPage = 20, entityType, publishedOnly = false } = options;

    const query = useQuery({
        queryKey: [searchSsqApiKey, searchTerm, page, itemsPerPage, entityType, publishedOnly],
        queryFn: async () => {
            const params: Record<string, any> = {
                page,
                itemsPerPage,
            };

            // Only add search parameter if searchTerm is provided and has minimum length
            if (searchTerm && searchTerm.length >= 2) {
                params.search = searchTerm;
            }

            if (entityType) {
                params["ssqTargetEntities.entityType"] = entityType;
            }

            if (publishedOnly) {
                params["exists[currentVersion]"] = true;
            }

            return await sureApiCall.get("/api/ssqs", {
                params,
            });
        },
        select: (response) => {
            if (!response || !response.data) {
                return {
                    ssqs: [],
                    totalItems: 0,
                };
            }

            // Maintenant on reçoit du JSON-LD avec la structure Hydra
            const ssqs = response.data["hydra:member"] || [];
            const totalItems = response.data["hydra:totalItems"] || 0;

            const data = zodParseWithLog(ssqs, SsqAutocompleteSchema, searchSsqApiKey);

            return {
                ssqs: data,
                totalItems,
            };
        },
        // Only search if term is 2+ chars or empty (for initial load)
        enabled: enabled && (!searchTerm || searchTerm.length >= 2),
        staleTime: 30000, // Cache for 30 seconds
        cacheTime: 300000, // Keep in cache for 5 minutes
    });

    return {
        ssqs: query.data?.ssqs || [],
        totalItems: query.data?.totalItems || 0,
        ...query,
    };
};
